import React, { useState, useEffect, ReactElement, useMemo } from "react";
import {
  Autocomplete,
  Chip,
  FormControl,
  TextField,
  CircularProgress,
} from "@mui/material";
import View from "../../../types/view/View";
import { useViews } from "../../../hooks/useView";

export type ViewsSelectProps = {
  currentViews: string[];
  label: string;
  placeholder: string;
  customerId: string | undefined;
  onChange: (selectedViews: string[]) => void;
  icon?: ReactElement;
};

export default function ViewsSelect({
  customerId,
  currentViews,
  label,
  placeholder,
  onChange,
  icon,
}: ViewsSelectProps) {
  const [viewIds, setViewIds] = useState<string[]>(currentViews);

  const { data: allAvailableViews = [], isLoading: isViewsLoading } = useViews(
    customerId ?? ""
  );

  const filteredViewOptions = useMemo(() => {
    return allAvailableViews.filter(
      (view) => !currentViews?.includes(view.viewId)
    );
  }, [allAvailableViews, currentViews]);

  useEffect(() => {
    setViewIds(currentViews);
  }, [currentViews]);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: View[]) => {
    const updatedViewIds = newValue.map((view) => view.viewId);
    setViewIds(updatedViewIds);
    onChange(updatedViewIds);
  };

  return (
    <FormControl fullWidth>
      <Autocomplete
        multiple
        id="views-autocomplete"
        options={filteredViewOptions}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.viewId === value.viewId}
        value={allAvailableViews.filter((view) =>
          viewIds.includes(view.viewId)
        )}
        onChange={handleChange}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              label={option.name}
              {...getTagProps({ index })}
              onDelete={() => {
                const updatedViewIds = viewIds.filter(
                  (viewId) => viewId !== option.viewId
                );
                setViewIds(updatedViewIds);
                onChange(updatedViewIds);
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={label}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {icon}
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {isViewsLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </FormControl>
  );
}
